import React, { useState } from 'react';
import { TextField, Button, Container, Box, FormControlLabel, Checkbox, Paper } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AuthoringAssistantPanel from '../AI/AuthoringAssistantPanel';
import { AuthoringAssistantGeneratedDraftResponse } from '../../services/authoringAssistantService';

interface LessonFormData {
  title: string;
  description: string;
  order: number;
  isActive: boolean;
}

const LessonForm: React.FC<{
  courseId?: number;
  lessonId?: number;
  initialData?: {
    title: string;
    description: string;
    order: number;
    isActive: boolean;
  };
  suggestedOrder?: number;
  assistantInitialPrompt?: string;
  assistantAutoGenerate?: boolean;
  onSubmit: (data: {
    title: string;
    description: string;
    order: number;
    isActive: boolean;
  }) => void;
}> = ({ courseId, lessonId, initialData, suggestedOrder, assistantInitialPrompt, assistantAutoGenerate = false, onSubmit }) => {
  const [formData, setFormData] = useState(initialData || {
    title: '',
    description: '',
    order: suggestedOrder || 1,
    isActive: true,
  });

  // Update order when suggestedOrder changes and we're creating a new lesson
  React.useEffect(() => {
    if (!initialData && suggestedOrder) {
      setFormData(prev => ({ ...prev, order: suggestedOrder }));
    }
  }, [suggestedOrder, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'order' ? parseInt(value, 10) : value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleApplyDraft = (draft: AuthoringAssistantGeneratedDraftResponse) => {
    if (!draft.lesson) {
      return;
    }

    setFormData((previousData: LessonFormData) => ({
      ...previousData,
      title: draft.lesson?.title || previousData.title,
      description: draft.lesson?.description || previousData.description,
    }));
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexDirection: { xs: 'column', xl: 'row' } }}>
        <Paper elevation={1} sx={{ p: 3, flex: 1, width: '100%' }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Oppitunnin nimi"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Kuvaus"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              multiline
              rows={4}
              fullWidth
            />
            <TextField
              label="Järjestys"
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                  name="isActive"
                  color="primary"
                />
              }
              label="Aktiivinen"
            />
            <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>
              Tallenna
            </Button>
          </Box>
        </Paper>

        <AuthoringAssistantPanel
          courseId={courseId}
          lessonId={lessonId}
          draftLesson={formData}
          draftTarget="lesson"
          onApplyDraft={handleApplyDraft}
          initialPrompt={assistantInitialPrompt}
          autoGenerateInitialDraft={assistantAutoGenerate}
        />
      </Box>
    </Container>
  );
};

export default LessonForm;
